import React, { useCallback, useMemo } from 'react';
import SubPage, { PageActions } from '#/common/components/SubPage';
import WorkOrders from '../components/WorkOrders';
import { Tab } from '#/common/components/SubPage/SubPageProps';
import useDocumentTitle from '#/common/hooks/useDocumentTitle';
import If from '#/common/components/If';
import { Navigate, useSearchParams } from 'react-router-dom';
import WOTemplate from '../../wo-template/components/WOTemplates';
import useWorkOrderPropsCounts from '../hooks/useWorkOrderPropsCountsQuery';
import useSession from '#/common/hooks/useSession';
import { UserRole } from '#/common/types/userTypes';
import { PageAction } from '#/app/providers/PageProvider';
import WorkOrderDataGridFsmFlat from '../components/WorkOrderDataGrid/WorkOrderDataGridFsmFlat';
// WorkOrderDataGridFsm (grouped) is kept for reference but not used on the FSM tab.
// import WorkOrderDataGridFsm from '../components/WorkOrderDataGrid/WorkOrderDataGridFsm';

export type WorkOrdersTab = 'list' | 'templates' | 'fsm-list';

export const defaultTab: WorkOrdersTab = 'list';

export interface WorkOrdersProps {
  readonly tab: WorkOrdersTab;
}

const WorkOrdersPage: React.FC<WorkOrdersProps> = ({ tab }) => {
  const { user } = useSession();
  const isRoleTechnician = !!user && user.role === UserRole.technician && user.id;
  const [searchParams] = useSearchParams();
  const assetId = searchParams.get('assetId') || undefined;
  const slaMetricId = searchParams.get('slaMetricId') || undefined;
  const slaBucket = searchParams.get('slaBucket') || undefined;
  const tabs = [
    ...(isRoleTechnician
      ? []
      : [
          {
            key: 'fsm-list' as const,
            text: 'FSM Work Orders',
            extra: {
              newPath: '/work-orders/fsm/new',
              newLabel: 'FSM Work Order',
            },
          },
        ]),
    {
      key: 'list',
      text: isRoleTechnician ? 'My Work Orders' : 'TMM Work Orders',
      extra: {
        newPath: '/work-orders/new',
        newLabel: 'Work Order',
      },
    },
    {
      key: 'templates',
      text: 'Templates',
      extra: {
        newPath: '/work-orders/templates/new',
        newLabel: 'Template',
      },
    },
  ] satisfies Tab<WorkOrdersTab>[];

  const selectedTab = tabs.find((t) => t.key === tab);
  const title =
    selectedTab?.key === 'list' || selectedTab?.key === 'fsm-list'
      ? selectedTab?.text
      : `Work Order ${selectedTab?.text}`;
  useDocumentTitle(title);

  const { workOrders = 0, fsmLineItemsWorkOrders = 0, templates = 0 } = useWorkOrderPropsCounts();

  const getPath = useCallback(
    (tab: string) => {
      if (tab === 'list' && isRoleTechnician) {
        const filters = ['assignedToId', '=', user?.id];
        const params = new URLSearchParams();
        params.set('filters', JSON.stringify(filters));
        return `list?${params.toString()}`;
      }

      return tab;
    },
    [user, isRoleTechnician],
  );

  const workOrdersTabs = useMemo(
    () =>
      tabs.map((t) => {
        if (t.key === 'list') {
          return {
            ...t,
            count: workOrders,
            path: getPath(t.key),
          };
        }
        if (t.key === 'fsm-list') {
          return {
            ...t,
            count: fsmLineItemsWorkOrders ?? 0,
            path: 'fsm-list',
          };
        }
        if (t.key === 'templates') {
          return {
            ...t,
            count: templates,
            path: getPath(t.key),
          };
        }
        return t;
      }),
    [isRoleTechnician, workOrders, fsmLineItemsWorkOrders, templates, getPath],
  ) as Tab[];

  const assetParam = assetId ? `?assetId=${assetId}` : '';
  const newPath = selectedTab?.key && `${selectedTab.extra.newPath}${assetParam}`;

  const isFsmTab = tab === 'fsm-list';

  const actions = useMemo<PageAction[]>(
    () => [
      {
        id: 'new-wo-item',
        alwaysShow: true,
        type: 'button',
        props: {
          type: 'submit',
          icon: 'add',
          label: `Create New ${selectedTab?.extra.newLabel}`,
          responsive: true,
          path: newPath,
          disabled: isFsmTab,
        },
      },
    ],
    [newPath, selectedTab, isFsmTab],
  );
  const invalidTab = tabs.every((t) => t.key !== tab);
  if (invalidTab) {
    const redirectTo = `../${defaultTab}`;
    return <Navigate to={redirectTo} replace />;
  }

  // SLA metrics apply only to TMM Work Orders — redirect away from FSM tab
  if ((slaMetricId || slaBucket) && tab === 'fsm-list') {
    return <Navigate to={`../list?${searchParams.toString()}`} replace />;
  }

  return (
    <SubPage
      title={title}
      tabs={workOrdersTabs}
      selectedTab={tab}
      type="list"
      hideSelectToggle={isFsmTab}>
      <PageActions actions={actions} />
      <If condition={tab === 'list'}>
        <WorkOrders hideNewAction showOnlyInternal={!slaMetricId && !slaBucket} />
      </If>
      <If condition={tab === 'fsm-list'}>
        <WorkOrderDataGridFsmFlat />
      </If>
      <If condition={tab === 'templates'}>
        <WOTemplate />
      </If>
    </SubPage>
  );
};

export default React.memo(WorkOrdersPage);
